import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { sortBy } from 'lodash'
import { connectMenu, Highlight } from 'react-instantsearch-dom'
import { link, text } from 'chaoskit/src/assets/styles/utility'
import { StylesAvatarVariables } from 'chaoskit/src/components/Avatar'

import { StylesAlgoliaVariables } from './algolia-global'

import Image from '~components/Image'
import useAuthor, { getAuthor } from '~hooks/useAuthor'
import TeaserCard from '~components/blog/TeaserCard'

const CategoryMenu = ({ items, refine, createURL }) => {
  const theme = useTheme()

  return items.length > 0 ? (
    <nav
      css={{
        display: 'grid',
        gridAutoFlow: 'column',
        gridAutoColumns: 'auto',
        gap: 2,
        background: theme.color.border.base,
        textAlign: 'center',
      }}
    >
      {items.map((item) => (
        <a
          key={item.value}
          href={createURL(item.value)}
          onClick={(event) => {
            event.preventDefault()
            refine(item.value)
          }}
          css={[
            {
              background: theme.color.light.base,
              ...text.expanded(theme),
              color: theme.fontColor.base,
              ...theme.text.small,
              paddingTop: theme.space.small,
              paddingBottom: theme.space.small,
              paddingLeft: theme.space.small + theme.space.xsmall,
              paddingRight: theme.space.small + theme.space.xsmall,
              position: 'relative',
              fontWeight: theme.fontWeight.bold,

              '&:hover, &:focus': {
                color: theme.color.primary.base,
              },
            },
            item.isRefined && {
              color: theme.color.primary.base,

              '&::before': {
                content: "''",
                height: 2,
                display: 'block',
                left: theme.space.small + theme.space.xsmall,
                right: theme.space.small + theme.space.xsmall,
                background: theme.color.primary.base,
                position: 'absolute',
                bottom: 0,
              },
            },
          ]}
        >
          {item.label}
        </a>
      ))}
    </nav>
  ) : (
    <p css={{ ...theme.text.small, color: theme.fontColor.muted }}>
      No matching categories
    </p>
  )
}

CategoryMenu.propTypes = {
  items: PropTypes.array,
  refine: PropTypes.func,
  createURL: PropTypes.func,
}

export const CustomCategoryMenu = connectMenu(CategoryMenu)

const AuthorMenu = ({ items, refine, createURL }) => {
  const theme = useTheme()

  const authors = useAuthor()

  if (items.length === 0) return null

  return (
    <nav
      css={{
        display: 'grid',
        gridAutoFlow: 'column',
        gridAutoColumns: '1fr',
        overflowX: 'auto',
        marginTop: theme.space.large,
        gap: theme.space.base,
        whiteSpace: 'nowrap',
        maskImage: `linear-gradient(
                    90deg,
                    transparent,
                    ${theme.color.light.base} 10%,
                    ${theme.color.light.base} 90%,
                    transparent
                  )`,
      }}
    >
      <div css={{ width: 1 }} /> {/* Dummy spacer */}
      {sortBy(items, (item) => item.label).map((item) => {
        const authorMetadata = getAuthor(authors, item.label)

        return (
          <a
            key={item.value}
            href={createURL(item.value)}
            onClick={(event) => {
              event.preventDefault()

              refine(item.value)
            }}
            css={[
              link.reset(theme),
              {
                display: 'inline-grid',
                gap: theme.space.small,
                justifyItems: 'center',

                '&:hover, &:focus': {
                  img: {
                    transform: 'scale(1.05)',
                  },
                },
              },
            ]}
          >
            <div
              css={[
                {
                  position: 'relative',
                  display: 'inline-block',
                },

                item.isRefined && {
                  '&::after': {
                    content: "''",
                    width: StylesAlgoliaVariables.notificationSize,
                    height: StylesAlgoliaVariables.notificationSize,
                    position: 'absolute',
                    background: theme.brand.blue,
                    borderRadius: theme.borderRadius.rounded,
                    zIndex: 2,
                    top: 0,
                    left: -StylesAlgoliaVariables.notificationSize / 4,
                    border: `2px solid ${theme.color.light.base}`,
                  },
                },
              ]}
            >
              <Image
                css={{
                  transition: `transform ${theme.timing.base} ${theme.transition.bounce}`,
                  transformOrigin: 'center center',
                  height: StylesAvatarVariables(theme).size.base,
                }}
                src={authorMetadata.frontmatter.photo}
                alt=""
              />
            </div>
            <div>
              <div
                css={{
                  fontWeight: theme.fontWeight.bold,
                  ...theme.text.small,
                }}
              >
                {item.label}
              </div>
            </div>
          </a>
        )
      })}
      <div css={{ width: 1 }} /> {/* Dummy spacer */}
    </nav>
  )
}

AuthorMenu.propTypes = {
  items: PropTypes.array,
  refine: PropTypes.func,
  createURL: PropTypes.func,
}

export const CustomAuthorMenu = connectMenu(AuthorMenu)

export const Hit = ({ hit }) => (
  <TeaserCard
    data={hit}
    title={<Highlight tagName="mark" attribute="title" hit={hit} />}
  />
)

Hit.propTypes = {
  hit: PropTypes.object,
}
